import React, {
  ReactNode, useCallback, useLayoutEffect, useRef,
} from 'react';

import { CustomAlert } from 'components/AlertsCollection';
import Snackbar from 'components/DesignSystem/Snackbar';
import { StudyActionKind } from 'components/Flux/Personalized/MyStudies/ActionsCell';
import EmptyCompanyState from 'components/Flux/Personalized/MyStudies/EmptyCompanyState';
import EmptyFilterState from 'components/Flux/Personalized/MyStudies/EmptyFilterState';
import ListErrorBlock from 'components/Flux/Personalized/MyStudies/ListErrorBlock';
import ListSkeleton from 'components/Flux/Personalized/MyStudies/ListSkeleton';
import PendingOverlay from 'components/Flux/Personalized/MyStudies/PendingOverlay';
import StaleBanner from 'components/Flux/Personalized/MyStudies/StaleBanner';
import StudyList from 'components/Flux/Personalized/MyStudies/StudyList';
import { ActionAlert } from 'components/Flux/Personalized/MyStudies/useActionFeedback';
import { MyStudiesListResult } from 'helper/flux/myStudiesAdapter';
import { StudyViewer } from 'helper/flux/myStudyRow';
import { MyStudiesListState } from 'utils/hooks/flux/personalized/useMyStudiesListState';
import { MyStudy } from 'types/Flux/Personalized/myStudies';
import { MyStudiesFilters, MyStudiesFiltersChange } from 'types/Flux/Personalized/myStudiesFilters';

/** Raiz da faixa de erro (`StaleBanner`). */
const STALE_BANNER_SELECTOR = '.my-studies-stale-banner';

export interface ListZoneProps {
  /** Estado visual da lista (`useMyStudiesPage().state`). */
  state: MyStudiesListState;
  /** Última lista recebida (`useMyStudiesPage().list`). */
  list: MyStudiesListResult | undefined;
  /** Há lista na tela (`useMyStudiesPage().hasListOnScreen`). */
  hasListOnScreen: boolean;
  /** A lista é de uma página além da última: skeleton até a correção chegar. */
  isBeyondLastPage: boolean;
  /** Lista esmaecida e inerte: consulta nova a caminho ou busca esperando o debounce. */
  pending: boolean;
  /** Quem vê a tela, memoizado. */
  viewer: StudyViewer;
  /** Filtros APLICADOS (`useMyStudiesFilters().filters`): textos e ações do vazio (#2003). */
  filters: MyStudiesFilters;
  /** Nome do usuário filtrado em Criado por (#2000), para o subtítulo do vazio. */
  userLabel?: string;
  /** `setFilters` do hook: a ação principal do vazio ("Ver esta semana"…). */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
  /** `clearFilters` do hook: "Limpar filtros" do vazio. */
  onClearFilters: () => void;
  /**
   * A resposta na tela é de um escopo sem nenhum estudo: o vazio é o estado 4. Calculado uma vez,
   * na casca (`isCompanyWithoutStudies`), que também esconde a barra de filtros com ele.
   */
  companyWithoutStudies: boolean;
  /** "Agora" das linhas (`useMyStudiesPage().nowMs`). */
  nowMs: number;
  /**
   * Última atualização bem-sucedida do que a lista mostra, para a faixa de erro: o
   * `lastUpdatedAt` do polling (#2004, D03), `max(fulfilledTimeStamp da lista, último status)`.
   */
  lastUpdatedAt?: number;
  /**
   * O polling de status falhou (`polling.error`, #2004): a faixa aparece também com a lista em
   * dia (`ready`). Uma faixa só, com a lista em `error-stale` ou não.
   */
  statusFailed?: boolean;
  /**
   * Chave da faixa (`useMyStudiesAutoRefresh().staleBannerKey`): muda quando o status falha de
   * novo depois de um "Tentar de novo", e a faixa remonta para o `role="alert"` anunciar a falha
   * outra vez. As falhas dos ciclos automáticos não a mudam.
   */
  staleBannerKey?: string;
  /**
   * "Tentar de novo" do bloco e da faixa: refaz o que falhou (a casca decide entre o
   * `refetchPage` e o `retry` do polling).
   */
  onRetry: () => void;
  /** Resultado de cada ação da linha (`useActionFeedback().showFeedback`), estável. */
  onFeedback: (feedback: CustomAlert) => void;
  /** Erro ou aviso de uma ação, que fica acima da lista até ser fechado. */
  actionAlert: ActionAlert | null;
  /** Fecha o `actionAlert`. */
  onDismissActionAlert: () => void;
  /** Ação concluída, para o destaque da linha (#2004). Estável, se vier. */
  onActionSuccess?: (studyId: number, kind: StudyActionKind) => void;
  /** Conteúdo da linha aberta (#2002), repassado à `StudyList`. Estável, se vier. */
  renderExpanded?: (study: MyStudy) => ReactNode;
  /** Linhas destacadas (#2004, `polling.changedIds`), repassadas à `StudyList`. Estável. */
  highlightedIds?: ReadonlySet<number>;
}

/**
 * Zona da lista da aba Meus Estudos (#1999): faixa de erro, alerta de erro de uma ação (#1994) e,
 * dentro do `PendingOverlay`, o conteúdo do estado: skeleton (primeira carga, retry sem lista ou
 * página além da última), bloco de erro, um dos vazios ou as linhas.
 *
 * Faixa "Status não atualizado desde HH:MM." (#1992, #2004): UMA só, com a lista na tela e em
 * `error-stale` (a lista falhou) ou em `ready` com `statusFailed` (o polling falhou). Sai no
 * `pending` (o usuário pediu outra lista ou "Atualizar"), e sem lista na tela fica só o bloco.
 * A falha que responde a um "Tentar de novo" (outra `staleBannerKey`) remonta a faixa, para ser
 * anunciada de novo; nas falhas dos ciclos automáticos, a faixa fica a mesma.
 *
 * Vazios (#2003), só com a resposta dos filtros na tela: total 0 com `companyWithoutStudies` é o
 * estado 4 (`EmptyCompanyState`); senão, o estado 3 (`EmptyFilterState`). No `pending`, a lista
 * vazia na tela é a do filtro anterior e os textos já seriam os do novo: fica o skeleton até a
 * resposta.
 *
 * Foco: a zona recebe o foco (`tabIndex={-1}`) quando o elemento focado vai sumir ou ficar inerte,
 * para ele não cair no `<body>`:
 * - "Tentar de novo" do bloco (vira skeleton) e da faixa da lista (some no `pending`). Na faixa
 *   só do status, o foco fica no botão: a lista não esmaece e a faixa fica até a resposta. Se o
 *   status falha de novo, a faixa remonta e o foco vai para o botão dela; se volta, a faixa sai e
 *   o foco vai para a zona;
 * - ao esmaecer com o foco dentro da área inerte (voltar no navegador, debounce da busca, modal
 *   devolvendo o foco a um botão da linha);
 * - ao excluir um estudo, na hora;
 * - quando o elemento que tinha o foco sai da tela com a linha: a exclusão (o modal, dentro da
 *   linha, devolve o foco ao "Excluir" dela ao fechar) e o 404 dos cenários (#2002), que refaz a
 *   lista sem o estudo. Foco que o usuário levou para fora da zona fica onde está.
 * - nas ações do vazio por filtro, antes de mudar o filtro: o cartão some com o botão;
 * - quando o vazio vira skeleton no pending (ex.: voltar no navegador) e o foco caiu no `<body>`.
 */
export default function ListZone({
  state,
  list,
  hasListOnScreen,
  isBeyondLastPage,
  pending,
  viewer,
  filters,
  userLabel,
  onFiltersChange,
  onClearFilters,
  companyWithoutStudies,
  nowMs,
  lastUpdatedAt,
  onRetry,
  onFeedback,
  actionAlert,
  onDismissActionAlert,
  onActionSuccess,
  renderExpanded,
  highlightedIds,
  statusFailed = false,
  staleBannerKey,
}: ListZoneProps) {
  const zoneRef = useRef<HTMLDivElement>(null);
  const focusZone = useCallback(() => zoneRef.current?.focus({ preventScroll: true }), []);

  const retry = useCallback(() => {
    focusZone();
    onRetry();
  }, [focusZone, onRetry]);

  // Ações do vazio por filtro: o cartão some (skeleton no pending, linhas ou outro vazio), e o
  // foco no botão clicado cairia no <body>
  const changeFiltersFromEmpty = useCallback((change: MyStudiesFiltersChange) => {
    focusZone();
    onFiltersChange(change);
  }, [focusZone, onFiltersChange]);
  const clearFiltersFromEmpty = useCallback(() => {
    focusZone();
    onClearFilters();
  }, [focusZone, onClearFilters]);

  const handleActionSuccess = useCallback((studyId: number, kind: StudyActionKind) => {
    if (kind === 'delete') focusZone();
    onActionSuccess?.(studyId, kind);
  }, [focusZone, onActionSuccess]);

  // Último elemento da zona com o foco: um botão da linha, do painel aberto ou de um modal da
  // linha (o foco de um portal também sobe pela árvore do React), e se ele estava na faixa
  const lastFocusedRef = useRef<Element | null>(null);
  const focusWasInBannerRef = useRef(false);
  const rememberFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    lastFocusedRef.current = event.target;
    focusWasInBannerRef.current = event.target.closest(STALE_BANNER_SELECTOR) !== null;
  }, []);

  const showsStaleBanner = state === 'error-stale' || (statusFailed && state === 'ready');
  // Só o status falhou: a lista não esmaece, e o "Tentar de novo" deixa o foco no botão
  const statusOnlyBanner = showsStaleBanner && state !== 'error-stale';

  // O elemento que tinha o foco saiu da tela: com a linha (a lista mudou: exclusão, 404 dos
  // cenários) ou com a faixa (o status voltou ou falhou de novo e ela remontou). O foco que caiu no
  // <body> vai para o botão da faixa nova, se ele estava na faixa, ou para a zona. Se o elemento
  // continua na tela, o foco saiu dele por escolha do usuário e fica onde está
  useLayoutEffect(() => {
    const lastFocused = lastFocusedRef.current;
    if (lastFocused === null || lastFocused.isConnected) return;
    lastFocusedRef.current = null;
    if (document.activeElement !== null && document.activeElement !== document.body) return;
    const bannerButton = focusWasInBannerRef.current
      ? zoneRef.current?.querySelector<HTMLElement>(`${STALE_BANNER_SELECTOR} button`)
      : null;
    if (bannerButton) bannerButton.focus();
    else focusZone();
  }, [list, showsStaleBanner, staleBannerKey, focusZone]);

  const retryStale = useCallback(() => {
    if (statusOnlyBanner) onRetry();
    else retry();
  }, [statusOnlyBanner, onRetry, retry]);

  // Um vazio estava na tela no commit anterior? No pending ele vira skeleton e desmonta junto com o
  // botão que tinha o foco (ex.: voltar no navegador). Lido no efeito abaixo, gravado no fim
  const emptyWasOnScreen = useRef(false);

  // Antes da pintura: o foco num botão da área que acabou de ficar inerte vai para a zona; o foco
  // que caiu no <body> porque o cartão do vazio desmontou, também
  useLayoutEffect(() => {
    if (!pending) return;
    const active = document.activeElement;
    if (active && active.closest('[inert]') && zoneRef.current?.contains(active)) focusZone();
    else if (emptyWasOnScreen.current && (active === null || active === document.body)) focusZone();
  }, [pending, focusZone]);

  let content: ReactNode;
  let showsEmpty = false;
  if (state === 'initial') {
    content = <ListSkeleton />;
  } else if (!hasListOnScreen || list === undefined) {
    content = <ListErrorBlock onRetry={retry} />;
  } else if (isBeyondLastPage) {
    content = <ListSkeleton />;
  } else if (list.total === 0 && pending) {
    content = <ListSkeleton />;
  } else if (list.total === 0 && companyWithoutStudies) {
    showsEmpty = true;
    content = <EmptyCompanyState />;
  } else if (list.total === 0) {
    showsEmpty = true;
    content = (
      <EmptyFilterState
        filters={filters}
        isAdmin={viewer.isAdmin}
        userLabel={userLabel}
        onFiltersChange={changeFiltersFromEmpty}
        onClearFilters={clearFiltersFromEmpty}
      />
    );
  } else {
    content = (
      <StudyList
        studies={list.items}
        viewer={viewer}
        nowMs={nowMs}
        onFeedback={onFeedback}
        onActionSuccess={handleActionSuccess}
        renderExpanded={renderExpanded}
        highlightedIds={highlightedIds}
      />
    );
  }

  // Declarado depois do efeito do pending: ele lê o valor do commit anterior
  useLayoutEffect(() => {
    emptyWasOnScreen.current = showsEmpty;
  }, [showsEmpty]);

  return (
    <div
      ref={zoneRef}
      className="my-studies-list"
      role="region"
      aria-label="Lista de estudos"
      tabIndex={-1}
      data-testid="my-studies-list"
      onFocusCapture={rememberFocus}
    >
      {showsStaleBanner && (
        <StaleBanner key={staleBannerKey} lastUpdatedAt={lastUpdatedAt} onRetry={retryStale} />
      )}
      {actionAlert && (
        // A chave muda a cada aviso: o mesmo erro de novo é montado e anunciado de novo
        <div key={actionAlert.key} role="alert" data-testid="my-studies-action-alert">
          <Snackbar variant={actionAlert.variant} actionText="Fechar" onAction={onDismissActionAlert}>
            {actionAlert.message}
          </Snackbar>
        </div>
      )}
      <PendingOverlay pending={pending}>{content}</PendingOverlay>
    </div>
  );
}
